import { useEffect, useId, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { AlertCircle, AlertTriangle, CheckCircle2, Info, Monitor, MonitorOff, Send, X } from 'lucide-react';
import Modal from '../../ui/Modal';
import Button from '../../ui/Button';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { describeLibraryLine } from '../../../utils/stationLibrary';
import { TONE_CLASSES } from '../../../utils/tones';
import { formatWeekdayMonthDay, parseIsoDate } from '../../../utils/playlist';
import { stationLabel } from '../../../utils/playlistDelivery';
import { microInteractions, pageTransitions } from '../../../utils/animations';
import {
  COPY,
  DELIVERY_COPY,
  confirmLabel,
  confirmSummary,
  offlineStations,
  redeliveryNote,
  slideCaption,
} from './playlistCopy';

const TONE_ICONS = { success: CheckCircle2, warning: AlertTriangle, error: AlertCircle, muted: Info };
const SCROLL_LIST_OVER = 8;

const formatDeliveredAt = (ms) =>
  new Date(ms).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

function LibraryLine({ line, onNavigate }) {
  if (!line) {
    return <span className="block mt-1.5 h-3.5 w-40 max-w-full rounded shimmer bg-gray-200 dark:bg-elevated-dark-3" aria-hidden="true" />;
  }
  const Icon = TONE_ICONS[line.tone];
  return (
    <span className={`mt-1 flex items-start gap-1.5 text-body-sm ${TONE_CLASSES[line.tone]}`} data-testid="playlist-confirm-library" data-tone={line.tone}>
      <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
      <span className="min-w-0">
        {line.text}
        {line.invalid && (
          <>
            {' · '}
            <Link
              to="/media-stations"
              onClick={onNavigate}
              className="font-semibold underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
            >
              {DELIVERY_COPY.changeLibrary}
            </Link>
          </>
        )}
      </span>
    </span>
  );
}

function StationRadio({ station, name, checked, line, onSelect, onNavigate }) {
  return (
    <label className="block cursor-pointer" data-testid="playlist-confirm-station" data-agent-id={station.agent_id}>
      <input
        type="radio"
        name={name}
        value={station.agent_id}
        checked={checked}
        onChange={() => onSelect(station.agent_id)}
        className="peer sr-only"
      />
      <div className="relative flex items-start gap-3 squircle-sm p-4 border-2 border-transparent bg-gray-50 dark:bg-elevated-dark-2 transition-colors hover:border-gray-200 dark:hover:border-white/10 peer-checked:border-primary-500 peer-checked:bg-primary-50 dark:peer-checked:bg-primary-900/20 peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-elevated-dark-1">
        <span
          aria-hidden="true"
          className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-full border-2 flex items-center justify-center transition-colors ${
            checked ? 'border-primary-500' : 'border-gray-300 dark:border-white/25'
          }`}
        >
          <AnimatePresence>
            {checked && <motion.span {...microInteractions.checkboxPop} className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
          </AnimatePresence>
        </span>
        <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex-shrink-0" aria-hidden="true">
          <Monitor className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="flex-1 min-w-0 truncate font-semibold text-body text-gray-900 dark:text-gray-100">{stationLabel([station], station.agent_id)}</span>
            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-caption font-medium bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 flex-shrink-0">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" aria-hidden="true" />
              {DELIVERY_COPY.online}
            </span>
          </div>
          <LibraryLine line={line} onNavigate={onNavigate} />
        </div>
      </div>
    </label>
  );
}

function SheetBody({
  playlist,
  online,
  offline,
  selected,
  onSelect,
  stationSettings,
  stationSettingsStatus,
  lastDeliveredAt,
  summaryId,
  onNavigate,
}) {
  const radioName = useId();
  const captionId = useId();
  const { items } = playlist;
  const date = parseIsoDate(playlist.serviceDate);
  const scrolls = items.length > SCROLL_LIST_OVER;

  return (
    <div className="space-y-5">
      <div id={summaryId}>
        <p className="text-caption uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400" data-testid="playlist-confirm-name">
          {playlist.name}
          {date && <span className="font-normal"> · {formatWeekdayMonthDay(date)}</span>}
        </p>
        <p className="mt-1 text-body-sm text-gray-700 dark:text-gray-300">{confirmSummary(items)}</p>
      </div>

      <ol
        className={`space-y-1 ${scrolls ? 'max-h-64 overflow-y-auto pr-1' : ''}`}
        style={scrolls ? { maskImage: 'linear-gradient(to bottom, black calc(100% - 24px), transparent)', WebkitMaskImage: 'linear-gradient(to bottom, black calc(100% - 24px), transparent)' } : undefined}
        data-testid="playlist-confirm-items"
      >
        {items.map((item, index) => (
          <li key={item.itemKey} className="flex items-start gap-3 py-1" data-testid="playlist-confirm-item" data-design-id={item.designId}>
            <span className="mt-0.5 w-6 h-6 flex-shrink-0 rounded-full bg-gray-100 dark:bg-elevated-dark-2 text-caption font-semibold text-gray-700 dark:text-gray-200 flex items-center justify-center tabular-nums">
              {index + 1}
            </span>
            <span className="flex-1 min-w-0 text-body-sm font-medium text-gray-900 dark:text-gray-100 break-words">{item.title}</span>
            <span className="flex-shrink-0 text-caption text-gray-500 dark:text-gray-400 tabular-nums pt-0.5">{slideCaption(item.pageCount)}</span>
          </li>
        ))}
      </ol>

      <div>
        <p id={captionId} className="mb-2 text-caption uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400">
          {DELIVERY_COPY.stationCaption}
        </p>
        {online.length > 0 ? (
          <div role="radiogroup" aria-labelledby={captionId} className="space-y-2">
            {online.map((station) => (
              <StationRadio
                key={station.agent_id}
                station={station}
                name={radioName}
                checked={selected === station.agent_id}
                line={describeLibraryLine(stationSettings[station.agent_id], stationSettingsStatus)}
                onSelect={onSelect}
                onNavigate={onNavigate}
              />
            ))}
          </div>
        ) : (
          <div className="squircle-sm p-4 bg-gray-50 dark:bg-elevated-dark-2 flex items-start gap-3" data-testid="playlist-confirm-no-station">
            <MonitorOff className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-body-sm text-gray-700 dark:text-gray-200">{COPY.noStationOnline}</p>
              <Link
                to="/media-stations"
                onClick={onNavigate}
                className="mt-1 inline-block text-body-sm font-semibold text-primary-700 dark:text-primary-300 underline underline-offset-2 hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
              >
                {DELIVERY_COPY.goToStations}
              </Link>
            </div>
          </div>
        )}
        {offline.length > 0 && (
          <p className={`mt-2 text-caption ${TONE_CLASSES.muted}`} data-testid="playlist-confirm-offline">
            {offlineStations(offline.map((s) => stationLabel([s], s.agent_id)))}
          </p>
        )}
      </div>

      {lastDeliveredAt && (
        <p className={`flex items-start gap-1.5 text-body-sm ${TONE_CLASSES.muted}`} data-testid="playlist-confirm-redelivery">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
          {redeliveryNote(formatDeliveredAt(lastDeliveredAt))}
        </p>
      )}
    </div>
  );
}

function SheetFooter({ online, selected, count, onCancel, onConfirm, stacked }) {
  const station = online.find((s) => s.agent_id === selected);
  const reasonId = useId();
  return (
    <div className={stacked ? 'space-y-2' : 'flex items-center justify-end gap-3'}>
      {!station && online.length > 0 && (
        <p id={reasonId} className={`text-caption ${TONE_CLASSES.muted} ${stacked ? 'text-center' : 'mr-auto'}`}>
          {DELIVERY_COPY.chooseStation}
        </p>
      )}
      <div className={stacked ? 'flex flex-col-reverse gap-2' : 'flex items-center gap-3'}>
        <Button variant="secondary" onClick={onCancel} className={stacked ? 'w-full' : ''}>
          {COPY.cancel}
        </Button>
        <Button
          variant="primary"
          size="lg"
          disabled={!station}
          aria-describedby={!station && online.length > 0 ? reasonId : undefined}
          onClick={() => station && onConfirm(station.agent_id)}
          data-testid="playlist-confirm-submit"
          className={stacked ? 'w-full' : ''}
        >
          <Send className="w-4 h-4 mr-2" aria-hidden="true" />
          {station ? confirmLabel(count, stationLabel([station], station.agent_id)) : COPY.deliver}
        </Button>
      </div>
    </div>
  );
}

function PhoneSheet({ isOpen, onClose, title, summaryId, returnFocusRef, children, footer }) {
  const panelRef = useRef(null);
  const titleRef = useRef(null);
  const titleId = useId();
  const reduceMotion = useReducedMotion();
  useFocusTrap(panelRef, isOpen, { initialFocusRef: titleRef, returnFocusRef });

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50">
          <motion.div {...microInteractions.fadeIn} onClick={onClose} className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={summaryId}
            data-testid="playlist-confirm-sheet"
            {...(reduceMotion ? microInteractions.fadeIn : pageTransitions.slideFromBottom)}
            drag={reduceMotion ? false : 'y'}
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={0.2}
            onDragEnd={(event, { offset, velocity }) => {
              if (offset.y > 120 || velocity.y > 500) onClose();
            }}
            className="absolute inset-x-0 bottom-0 max-h-[92vh] flex flex-col bg-white dark:bg-elevated-dark-1 rounded-t-2xl shadow-soft-lg border-t border-gray-200 dark:border-white/10"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 0.5rem)' }}
          >
            <div className="h-6 flex items-center justify-center flex-shrink-0" aria-hidden="true">
              <span className="w-9 h-1 rounded-full bg-gray-300 dark:bg-white/20" />
            </div>
            <div className="flex items-center justify-between px-4 pb-3 border-b border-gray-200 dark:border-white/5">
              <h2 id={titleId} ref={titleRef} tabIndex={-1} className="text-heading font-semibold text-gray-900 dark:text-gray-100 focus:outline-none">
                {title}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label={DELIVERY_COPY.close}
                className="touch-target inline-flex items-center justify-center -mr-2 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-4 py-4">{children}</div>
            {/* The floating theme toggle owns the bottom-right 80 px (spec §2.1); keep the buttons above it. */}
            <div className="px-4 pt-3 pb-[84px] border-t border-gray-200 dark:border-white/5">{footer}</div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * S4: confirm what goes where before delivering. A Modal at ≥768 px, a
 * bottom sheet below; both carry `playlist-confirm-sheet`.
 */
export default function DeliverConfirmSheet({
  isOpen,
  layout,
  playlist,
  stations,
  stationSettings,
  stationSettingsStatus,
  defaultAgentId,
  lastDeliveredAt,
  returnFocusRef,
  onConfirm,
  onClose,
}) {
  const summaryId = useId();
  const online = stations.filter((s) => s.status === 'online');
  const offline = stations.filter((s) => s.status !== 'online');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    const onlineIds = stations.filter((s) => s.status === 'online').map((s) => s.agent_id);
    if (onlineIds.includes(defaultAgentId)) setSelected(defaultAgentId);
    else setSelected(onlineIds.length === 1 ? onlineIds[0] : null);
    // Chosen once per opening; later presence changes don't move the choice.
  }, [isOpen]);

  const body = (
    <SheetBody
      playlist={playlist}
      online={online}
      offline={offline}
      selected={online.some((s) => s.agent_id === selected) ? selected : null}
      onSelect={setSelected}
      stationSettings={stationSettings}
      stationSettingsStatus={stationSettingsStatus}
      lastDeliveredAt={lastDeliveredAt}
      summaryId={summaryId}
      onNavigate={onClose}
    />
  );

  const footer = (stacked) => (
    <SheetFooter
      online={online}
      selected={selected}
      count={playlist.items.length}
      onCancel={onClose}
      onConfirm={onConfirm}
      stacked={stacked}
    />
  );

  if (layout === 'sheet') {
    return (
      <PhoneSheet
        isOpen={isOpen}
        onClose={onClose}
        title={DELIVERY_COPY.confirmTitle}
        summaryId={summaryId}
        returnFocusRef={returnFocusRef}
        footer={footer(true)}
      >
        {body}
      </PhoneSheet>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={DELIVERY_COPY.confirmTitle}
      size="lg"
      className="md:max-w-2xl"
      describedBy={summaryId}
      returnFocusRef={returnFocusRef}
      closeLabel={DELIVERY_COPY.close}
      testId="playlist-confirm-sheet"
      footer={footer(false)}
    >
      {body}
    </Modal>
  );
}
